import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { isTestUser } from '@/utils/testUser';
import { PRICING_CONFIG } from '@/config/pricing';
import { getUserAccess, legacyTierFromAccess, type AccessProfileRow } from '@/lib/getUserAccess';

export type UserPlan = 'free' | 'pro' | 'copilot';
export type SubscriptionPlan = 'monthly' | 'yearly' | 'copilot_monthly' | 'copilot_yearly' | null;
export type AiPlan = 'free' | 'pro_tools' | 'plus';

interface ProfileData {
  fullName: string | null;
  email: string;
  phoneNumber: string | null;
}

interface SubscriptionData {
  plan: UserPlan;
  subscriptionPlan: SubscriptionPlan;
  paidFrom: string | null;
  paidUntil: string | null;
  hasWordPack: boolean;
  aiPlan: AiPlan;
}

interface UserContextType {
  currentUser: SubscriptionData;
  profile: ProfileData | null;
  loading: boolean;
  setUserPlan: (plan: UserPlan) => void;
  setUserSubscription: (subscriptionPlan: SubscriptionPlan, paidFrom: string | null, paidUntil: string | null) => Promise<void>;
  refreshSubscription: () => Promise<void>;
  updateProfile: (data: { fullName?: string; phoneNumber?: string }) => Promise<void>;
  setHasWordPack: (hasWordPack: boolean) => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

const DEFAULT_SUBSCRIPTION: SubscriptionData = {
  plan: 'free',
  subscriptionPlan: null,
  paidFrom: null,
  paidUntil: null,
  hasWordPack: false,
  aiPlan: 'free',
};

export function UserProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData>(DEFAULT_SUBSCRIPTION);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch profile from database + subscription from Lemon Squeezy
  const fetchSubscription = useCallback(async () => {
    if (!user) {
      setSubscriptionData(DEFAULT_SUBSCRIPTION);
      setProfile(null);
      setLoading(false);
      return;
    }

    try {
      // 1. Fetch ALL access-relevant columns from DB (per-product fields).
      //    Note: keep the .select() argument as a single literal string -
      //    Supabase's TypeScript inference relies on it.
      const { data, error } = await supabase
        .from('profiles')
        .select('full_name, email, phone_number, ai_plan, ai_subscription_status, ai_subscription_type, ai_period_start, ai_period_end, ai_lemonsqueezy_subscription_id, ai_daily_limit, ai_access_source, pro_subscription_status, pro_subscription_type, pro_period_start, pro_period_end, pro_lemonsqueezy_subscription_id, pro_access_source, has_document_pack, has_word_pack, document_pack_order_id, document_pack_purchase_date, document_pack_access_source, manual_ai_plus, manual_pro_access, manual_doc_pack, manual_access_note, manual_access_expires_at, subscription_plan, subscription_start_date, subscription_end_date')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching profile:', error);
      }

      if (data) {
        setProfile({
          fullName: data.full_name,
          email: data.email,
          phoneNumber: data.phone_number,
        });
      }

      // 2. Trigger LS reconciliation (writes per-product fields to DB) but
      //    don't depend on its return value - we read access from the DB row
      //    via the central getUserAccess() helper. This guarantees a single
      //    source of truth and avoids drift between client and server logic.
      try {
        const session = await supabase.auth.getSession();
        const accessToken = session.data.session?.access_token;
        if (accessToken) {
          await supabase.functions.invoke('check-subscription', {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
        }
      } catch (lsErr) {
        console.error('Error reconciling subscription with Lemon Squeezy:', lsErr);
      }

      // 3. Re-fetch the profile after reconciliation so we read the freshly
      //    written per-product fields. Skip the re-fetch if reconciliation
      //    failed - we'll just use the initial fetch.
      let finalRow: AccessProfileRow | null = (data as unknown as AccessProfileRow | null) ?? null;
      try {
        const { data: refreshed } = await supabase
          .from('profiles')
          .select('ai_plan, ai_subscription_status, ai_subscription_type, ai_period_start, ai_period_end, ai_daily_limit, ai_access_source, pro_subscription_status, pro_subscription_type, pro_period_start, pro_period_end, pro_access_source, has_document_pack, has_word_pack, document_pack_order_id, document_pack_purchase_date, document_pack_access_source, manual_ai_plus, manual_pro_access, manual_doc_pack, manual_access_expires_at, subscription_plan, subscription_start_date, subscription_end_date')
          .eq('user_id', user.id)
          .maybeSingle();
        if (refreshed) finalRow = refreshed as unknown as AccessProfileRow;
      } catch (e) {
        console.error('Failed to re-fetch profile after reconciliation:', e);
      }

      // 4. Compute access via central helper - SINGLE SOURCE OF TRUTH.
      const access = getUserAccess(finalRow);
      const legacy = legacyTierFromAccess(access);

      setSubscriptionData({
        plan: legacy.plan,
        subscriptionPlan:
          (access.pro.billing_type as SubscriptionPlan) ||
          ((finalRow?.subscription_plan as SubscriptionPlan) ?? null),
        paidFrom: access.pro.period_start || finalRow?.subscription_start_date || null,
        paidUntil: access.pro.period_end || null,
        hasWordPack: legacy.hasWordPack,
        aiPlan: legacy.aiPlan,
      });
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setSubscriptionData(DEFAULT_SUBSCRIPTION);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch subscription when user changes
  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Re-sync entitlements when the user returns to the tab after checkout.
  useEffect(() => {
    if (typeof window === 'undefined' || !user) return;

    const handleFocusRefresh = () => {
      void fetchSubscription();
    };

    const handleVisibilityRefresh = () => {
      if (document.visibilityState === 'visible') {
        void fetchSubscription();
      }
    };

    window.addEventListener('focus', handleFocusRefresh);
    document.addEventListener('visibilitychange', handleVisibilityRefresh);

    return () => {
      window.removeEventListener('focus', handleFocusRefresh);
      document.removeEventListener('visibilitychange', handleVisibilityRefresh);
    };
  }, [fetchSubscription, user]);

  const setUserPlan = (newPlan: UserPlan) => {
    setSubscriptionData(prev => ({ ...prev, plan: newPlan }));
  };

  const setUserSubscription = async (
    subscriptionPlan: SubscriptionPlan,
    paidFrom: string | null,
    paidUntil: string | null
  ) => {
    if (!user) {
      console.error('Cannot set subscription: No user logged in');
      return;
    }

    // Subscription activation is handled server-side via the Lemon Squeezy webhook.
    // Client just updates local state optimistically and then refreshes from DB.
    const planLevel: UserPlan = subscriptionPlan?.startsWith('copilot') ? 'copilot' : 'pro';
    setSubscriptionData(prev => ({
      ...prev,
      plan: planLevel,
      subscriptionPlan,
      paidFrom,
      paidUntil,
    }));

    // Refresh from database to get the actual server-side state.
    // The Lemon Squeezy webhook will have activated the subscription server-side.
    setTimeout(() => fetchSubscription(), 3000);
  };

  const setHasWordPack = async (hasWordPack: boolean) => {
    if (!user) {
      console.error('Cannot set word pack: No user logged in');
      return;
    }

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ has_word_pack: hasWordPack })
        .eq('user_id', user.id);

      if (error) {
        console.error('Error updating word pack status:', error);
        throw error;
      }

      // Update local state
      setSubscriptionData(prev => ({ ...prev, hasWordPack }));
    } catch (err) {
      console.error('Failed to update word pack status:', err);
      throw err;
    }
  };

  const updateProfile = async (data: { fullName?: string; phoneNumber?: string }) => {
    if (!user) {
      console.error('Cannot update profile: No user logged in');
      return;
    }

    try {
      const updateData: Record<string, string | null> = {};
      if (data.fullName !== undefined) updateData.full_name = data.fullName;
      if (data.phoneNumber !== undefined) updateData.phone_number = data.phoneNumber;

      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error updating profile:', error);
        throw error;
      }

      // Update local state
      setProfile(prev => prev ? {
        ...prev,
        fullName: data.fullName !== undefined ? data.fullName : prev.fullName,
        phoneNumber: data.phoneNumber !== undefined ? data.phoneNumber : prev.phoneNumber,
      } : null);
    } catch (err) {
      console.error('Failed to update profile:', err);
      throw err;
    }
  };

  const refreshSubscription = async () => {
    setLoading(true);
    await fetchSubscription();
  };

  // Apply test user override or simulatePaidUser mode
  const currentUser = useMemo((): SubscriptionData => {
    // Check if simulatePaidUser is enabled in config (with safe access)
    if (PRICING_CONFIG?.simulatePaidUser === true) {
      return {
        ...subscriptionData,
        plan: 'pro',
        hasWordPack: true,
      };
    }
    
    // Check for test user (only on lovable.app domains)
    const userEmail = user?.email || profile?.email;
    if (isTestUser(userEmail)) {
      return {
        ...subscriptionData,
        plan: 'pro',
        hasWordPack: true,
      };
    }
    return subscriptionData;
  }, [subscriptionData, user?.email, profile?.email]);

  return (
    <UserContext.Provider
      value={{
        currentUser,
        profile,
        loading,
        setUserPlan,
        setUserSubscription,
        refreshSubscription,
        updateProfile,
        setHasWordPack,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within UserProvider');
  }
  return context;
}